import type { AssetStore } from '../../assets/asset-store';
import { type Sprite, drawSprite } from '../../assets/atlas';
import type { Camera } from '../../render/camera';
import { GAME_FONT_FAMILY, SPRITE_FACING_OFFSET, TEAM_COLORS } from '../constants/feel';
import { shipScale } from '../constants/ships';
import { isBaseEntityId, type PlayerEntity, type World } from '../world';
import {
  EMOJI_BAR_ICON,
  EMOJI_SIZE,
  EMOJI_OFFSET_Y,
  EmojiField,
  emojiBarButton,
  emojiBarSlots,
  hitTest,
  EMOJI_COUNT,
} from './emoji';
import { BURSTS, ExplosionField } from './explosions';
import {
  DEFAULT_IMPACT_SCATTER,
  LASER_STYLE,
  LaserField,
  MISS_BEAM_LENGTH,
  laserAlpha,
} from './lasers';
import {
  DAMAGE_NUMBER_COLORS,
  DAMAGE_NUMBER_FONT_SIZE,
  DAMAGE_NUMBER_RISE_SPEED,
  DamageNumberField,
  damageNumberAlpha,
  damageNumberScatter,
} from './damage-numbers';
import { EffectRandom } from './random';
import {
  GLYPH_FONT_FAMILY,
  GLYPH_FONT_WEIGHT,
  RIBBONS,
  type Ribbon,
  TRAIL_PIECE_RADIUS,
  TRAIL_TOUCH_MARGIN,
  type TrailAnchor,
  TrailBuffer,
  angleDelta,
  hasTrail,
  ribbonFor,
  ribbonReach,
  trailInfillCount,
  trailSegment,
} from './trails';

const HIT_SCATTER: Record<string, number> = {
  player: 20,
  turret: 30,
  drone: 22,
  controlPoint: 50,
  soccerBall: 250,
  base: 250,
};

interface PendingBomb {
  readonly targetId: number;
  readonly at: number;
}

const SELF_ANCHOR = -1;

export class EffectSystem {
  private readonly explosions = new ExplosionField();
  private readonly lasers = new LaserField();
  private readonly emoji = new EmojiField();
  private readonly numbers = new DamageNumberField();
  private readonly trails = new TrailBuffer();
  private trailsMounted = false;
  private readonly random = new EffectRandom();
  private readonly pendingBombs: PendingBomb[] = [];
  private readonly trailAnchors = new Map<number, TrailAnchor>();

  private wasDead = false;
  private lastGeneration = -1;
  private detach: (() => void) | null = null;

  private pointerX = Number.NaN;
  private pointerY = Number.NaN;

  constructor(private readonly assets: AssetStore) {}

  get counts(): {
    explosions: number;
    lasers: number;
    emoji: number;
    pendingBombs: number;
    damageNumbers: number;
  } {
    return {
      explosions: this.explosions.count,
      lasers: this.lasers.count,
      emoji: this.emoji.count,
      pendingBombs: this.pendingBombs.length,
      damageNumbers: this.numbers.count,
    };
  }

  /** Digit keys echo an emoji locally: the server broadcasts an emoji to observers only, never back to the sender. */
  attach(canvas: HTMLCanvasElement, camera: Camera): void {
    const onPointerMove = (event: PointerEvent): void => {
      const rect = canvas.getBoundingClientRect();
      const ratio = canvas.width / Math.max(1, rect.width);
      this.pointerX = ((event.clientX - rect.left) * ratio) / camera.uiScale;
      this.pointerY = ((event.clientY - rect.top) * ratio) / camera.uiScale;
    };
    const onPointerLeave = (): void => {
      this.pointerX = Number.NaN;
      this.pointerY = Number.NaN;
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      const code = event.code;
      if (typeof code !== 'string' || !code.startsWith('Digit')) return;
      const index = Number(code.slice(5));
      if (Number.isInteger(index) && index >= 0 && index < EMOJI_COUNT) {
        this.emoji.add(null, index, performance.now());
      }
    };

    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('keydown', onKeyDown);
    this.detach = () => {
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('keydown', onKeyDown);
    };
  }

  dispose(): void {
    this.detach?.();
    this.detach = null;
  }

  clear(): void {
    this.explosions.clear();
    this.lasers.clear();
    this.emoji.clear();
    this.numbers.clear();
    this.trails.reset();
    this.trailAnchors.clear();
    this.pendingBombs.length = 0;
  }

  advance(world: World, camera: Camera, now: number): void {
    if (world.generation !== this.lastGeneration) {
      this.lastGeneration = world.generation;
      this.clear();
    }

    for (const event of world.visualEvents) {
      switch (event.kind) {
        case 'hit':
          this.spawnBeam(world, event.shooterId, event.targetId, now, true);
          break;
        case 'miss':
          this.spawnBeam(world, event.shooterId, undefined, now, false);
          break;
        case 'death':
          this.spawnDeath(world, event.entityId, now);
          break;
        case 'emoji':
          if (event.entityId !== undefined && event.emojiIndex !== undefined) {
            this.emoji.add(event.entityId, event.emojiIndex, now);
          }
          break;
        case 'suicideBomb':
          if (event.targetId !== undefined) {
            this.pendingBombs.push({ targetId: event.targetId, at: event.arrivesAt ?? now });
          }
          break;

        case 'damageNumber': {
          if (event.entityId === undefined || (event.amount ?? 0) === 0) break;
          const position = this.positionOf(world, event.entityId, now);
          if (position === null) break;
          const [sx, sy] = damageNumberScatter(event.entityId, event.at);
          this.numbers.add(position.x + sx, position.y + sy, event.amount ?? 0, now);
          break;
        }
      }
    }
    world.visualEvents.length = 0;

    /** Own death produces no visual event; the dead-flag transition is the only signal there is. */
    if (world.dead && !this.wasDead) {
      this.explosions.burst(BURSTS.player, world.selfX, world.selfY, now, this.random);
    }
    this.wasDead = world.dead;

    this.resolveBombs(world, now);
    this.explosions.advance(now);
    this.lasers.advance(now);
    this.emoji.advance(now);
    this.numbers.advance(now);
    this.accumulateTrails(world, camera);
  }

  private resolveBombs(world: World, now: number): void {
    let write = 0;
    for (const bomb of this.pendingBombs) {
      if (now < bomb.at) {
        this.pendingBombs[write++] = bomb;
        continue;
      }
      const position = this.positionOf(world, bomb.targetId, now);
      if (position !== null) {
        this.explosions.burst(BURSTS.suicideBomb, position.x, position.y, now, this.random);
      }
    }
    this.pendingBombs.length = write;
  }

  private spawnDeath(world: World, entityId: number | undefined, now: number): void {
    if (entityId === undefined) return;
    const entity = world.entities.get(entityId);
    if (entity === undefined) return;
    const shape =
      entity.kind === 'turret'
        ? BURSTS.turret
        : entity.kind === 'drone'
          ? BURSTS.drone
          : BURSTS.player;
    this.explosions.burst(shape, entity.displayX, entity.displayY, now, this.random);
  }

  /** A miss travels a fixed distance along the shooter's heading, since a miss packet carries no endpoint. */
  private spawnBeam(
    world: World,
    shooterId: number | undefined,
    targetId: number | undefined,
    now: number,
    hit: boolean,
  ): void {
    if (shooterId === undefined) return;
    const from = this.positionOf(world, shooterId, now);
    if (from === null) return;

    let toX: number;
    let toY: number;
    let scatter = DEFAULT_IMPACT_SCATTER;

    if (hit && targetId !== undefined) {
      const to = this.positionOf(world, targetId, now);
      if (to === null) return;
      toX = to.x;
      toY = to.y;
      const kind =
        world.entities.get(targetId)?.kind ?? (isBaseEntityId(targetId) ? 'base' : undefined);
      if (targetId !== world.selfId && kind !== undefined) {
        scatter = HIT_SCATTER[kind] ?? DEFAULT_IMPACT_SCATTER;
      }
    } else {
      const angle = this.angleOf(world, shooterId);
      toX = from.x + Math.cos(angle) * MISS_BEAM_LENGTH;
      toY = from.y + Math.sin(angle) * MISS_BEAM_LENGTH;
    }

    if (scatter > 0) {
      toX += this.random.int(-scatter, scatter);
      toY += this.random.int(-scatter, scatter);
    }

    this.lasers.add(from.x, from.y, toX, toY, now);
    if (hit) this.explosions.burst(BURSTS.impact, toX, toY, now, this.random);
  }

  private positionOf(world: World, id: number, now: number): { x: number; y: number } | null {
    if (id === world.selfId) return { x: world.selfX, y: world.selfY };
    const entity = world.entities.get(id);
    if (entity !== undefined) return { x: entity.displayX, y: entity.displayY };
    return world.basePositionForEntityId(id, now);
  }

  private angleOf(world: World, id: number): number {
    if (id === world.selfId) return world.selfAngle;
    return world.entities.get(id)?.angle ?? 0;
  }

  /** Trails are shown only to the owner's own team — an enemy trail would give away a position the LOS mask is meant to hide. */
  private accumulateTrails(world: World, camera: Camera): void {
    const context = this.trails.advance(
      camera.deviceWidth,
      camera.deviceHeight,
      camera.centreX,
      camera.centreY,
      camera.worldScale,
    );
    if (context === null || !world.playing) return;

    const frame = this.trails.frameCount;
    context.save();
    context.scale(camera.worldScale, camera.worldScale);
    context.translate(-camera.offsetX, -camera.offsetY);

    const selfTrail = world.equipment.trail;

    if (!world.dead && world.selfTeam !== 2 && hasTrail(selfTrail)) {
      this.stampTrail(
        context,
        camera,
        SELF_ANCHOR,
        selfTrail,
        world.selfX,
        world.selfY,
        world.selfAngle,
        (1 + shipScale(world.selfShipId)) / 2,
        frame,
      );
    }

    for (const entity of world.entities.values()) {
      if (entity.kind !== 'player' || entity.id === world.selfId) continue;
      const player = entity as PlayerEntity;
      if (player.team !== world.selfTeam || player.health <= 0) continue;
      if (!hasTrail(player.trailProductId)) continue;
      this.stampTrail(
        context,
        camera,
        player.id,
        player.trailProductId,
        player.displayX,
        player.displayY,
        player.displayAngle,
        shipScale(player.shipId),
        frame,
      );
    }

    context.restore();

    for (const [key, anchor] of this.trailAnchors) {
      if (anchor.frame !== frame) this.trailAnchors.delete(key);
    }
  }

  private stampTrail(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    anchorKey: number,
    productId: number,
    x: number,
    y: number,
    angle: number,
    scale: number,
    frame: number,
  ): void {
    const { fromX, fromY, fromAngle } = trailSegment(
      this.trailAnchors.get(anchorKey),
      x,
      y,
      angle,
      frame,
    );
    this.trailAnchors.set(anchorKey, { x, y, angle, frame });

    const ribbon = ribbonFor(productId);
    if (ribbon === null) return;
    this.touchTrail(camera, fromX, fromY, x, y, ribbon, scale);

    if (ribbon.kind === 'colour') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      const radius = TRAIL_PIECE_RADIUS * scale;
      if (fromX === x && fromY === y) {
        ctx.fillStyle = ribbon.color;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      ctx.strokeStyle = ribbon.color;
      ctx.lineWidth = radius * 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(x, y);
      ctx.stroke();
      return;
    }

    if (frame % ribbon.every !== 0) return;

    if (ribbon.kind === 'glyph') {
      this.stampTrailGlyph(ctx, ribbon, x, y, angle, scale);
      return;
    }

    const sprite = this.assets.trailSprite(ribbon.file);
    if (sprite === null) return;

    const turn = angleDelta(fromAngle, angle);
    const infill =
      ribbon.every === 1
        ? trailInfillCount(Math.hypot(x - fromX, y - fromY), turn, ribbon.width, scale)
        : 0;

    for (let i = infill; i >= 0; i--) {
      const t = (infill + 1 - i) / (infill + 1);
      this.stampTrailSprite(
        ctx,
        ribbon,
        sprite,
        fromX + (x - fromX) * t,
        fromY + (y - fromY) * t,
        fromAngle + turn * t,
        scale,
      );
    }
  }

  private touchTrail(
    camera: Camera,
    fromX: number,
    fromY: number,
    x: number,
    y: number,
    ribbon: Ribbon,
    scale: number,
  ): void {
    const reach = ribbonReach(ribbon) * scale + TRAIL_TOUCH_MARGIN;
    const worldScale = camera.worldScale;
    this.trails.touch(
      (Math.min(fromX, x) - reach - camera.offsetX) * worldScale,
      (Math.min(fromY, y) - reach - camera.offsetY) * worldScale,
      (Math.max(fromX, x) + reach - camera.offsetX) * worldScale,
      (Math.max(fromY, y) + reach - camera.offsetY) * worldScale,
    );
  }

  private stampTrailGlyph(
    ctx: CanvasRenderingContext2D,
    ribbon: Extract<Ribbon, { kind: 'glyph' }>,
    x: number,
    y: number,
    angle: number,
    scale: number,
  ): void {
    const spread = ribbon.spread * scale;
    const setback = ribbon.setback * scale;
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.save();
    ctx.font = `${GLYPH_FONT_WEIGHT} ${String(Math.max(1, Math.round(ribbon.size * scale)))}px ${GLYPH_FONT_FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = this.random.next() < ribbon.headChance ? ribbon.headColor : ribbon.bodyColor;
    ctx.fillText(
      ribbon.glyphs.charAt(this.random.int(0, ribbon.glyphs.length - 1)),
      x - Math.cos(angle) * setback + this.random.range(-spread, spread),
      y - Math.sin(angle) * setback + this.random.range(-spread, spread),
    );
    ctx.restore();
  }

  private stampTrailSprite(
    ctx: CanvasRenderingContext2D,
    ribbon: Extract<Ribbon, { kind: 'sprite' }>,
    sprite: Sprite,
    x: number,
    y: number,
    angle: number,
    scale: number,
  ): void {
    const scatterAngle = ribbon.scatter > 0 ? this.random.range(0, Math.PI * 2) : angle;
    const setback = ribbon.scatter > 0 ? this.random.range(0, ribbon.scatter) : ribbon.setback;
    const drawAngle =
      ribbon.orientation === 'heading'
        ? angle + SPRITE_FACING_OFFSET
        : ribbon.orientation === 'random'
          ? this.random.range(0, Math.PI * 2)
          : 0;

    ctx.globalCompositeOperation = ribbon.under ? 'destination-over' : 'source-over';
    ctx.globalAlpha = 1;
    ctx.save();
    ctx.translate(x - Math.cos(scatterAngle) * setback, y - Math.sin(scatterAngle) * setback);
    ctx.rotate(drawAngle);
    const w = ribbon.width * scale;
    const h = ribbon.height * scale;
    drawSprite(ctx, sprite, -w / 2, -h / 2, w, h);
    ctx.restore();
    ctx.globalCompositeOperation = 'source-over';
  }

  mountTrails(host: HTMLElement): void {
    this.trailsMounted = true;
    this.trails.mount(host);
  }

  drawTrails(ctx: CanvasRenderingContext2D, camera: Camera): void {
    if (this.trailsMounted) return;
    const buffer = this.trails.canvas;
    if (buffer === null) return;
    const dirty = this.trails.dirty;
    if (dirty === null) return;

    const scaleX = camera.deviceWidth / buffer.width;
    const scaleY = camera.deviceHeight / buffer.height;
    const sx = Math.max(0, Math.floor(dirty.minX));
    const sy = Math.max(0, Math.floor(dirty.minY));
    const sw = Math.min(buffer.width, Math.ceil(dirty.maxX)) - sx;
    const sh = Math.min(buffer.height, Math.ceil(dirty.maxY)) - sy;
    if (sw <= 0 || sh <= 0) return;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(buffer, sx, sy, sw, sh, sx * scaleX, sy * scaleY, sw * scaleX, sh * scaleY);
    ctx.restore();
  }

  drawWorld(ctx: CanvasRenderingContext2D, world: World, now: number): void {
    ctx.save();
    ctx.font = `${String(DAMAGE_NUMBER_FONT_SIZE)}px ${GAME_FONT_FAMILY}, sans-serif`;
    ctx.textAlign = 'center';
    for (const number of this.numbers.all) {
      const age = now - number.at;
      const alpha = damageNumberAlpha(age);
      if (alpha <= 0) continue;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = number.amount < 0 ? DAMAGE_NUMBER_COLORS.heal : DAMAGE_NUMBER_COLORS.damage;
      const text = `${number.amount < 0 ? '+ ' : '- '}${String(Math.abs(number.amount))}`;
      ctx.fillText(text, number.x, number.y - (age / 1000) * DAMAGE_NUMBER_RISE_SPEED);
    }
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = LASER_STYLE.color;
    ctx.lineWidth = LASER_STYLE.lineWidth;
    for (const laser of this.lasers.all) {
      ctx.globalAlpha = laserAlpha(laser, now);
      ctx.beginPath();
      ctx.moveTo(laser.x1, laser.y1);
      ctx.lineTo(laser.x2, laser.y2);
      ctx.stroke();
    }
    ctx.restore();

    ctx.save();
    for (const explosion of this.explosions.all) {
      const alpha = this.explosions.alphaOf(explosion, now);
      if (alpha <= 0) continue;
      const sprite = this.assets.explosionSprite(explosion.sprite);
      ctx.globalAlpha = alpha;
      ctx.save();
      ctx.translate(explosion.x, explosion.y);
      ctx.rotate(explosion.angle);
      if (sprite !== null) {
        drawSprite(
          ctx,
          sprite,
          -explosion.size / 2,
          -explosion.size / 2,
          explosion.size,
          explosion.size,
        );
      } else {
        ctx.fillStyle = '#FFB84D';
        ctx.beginPath();
        ctx.arc(0, 0, explosion.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = 1;
    for (const active of this.emoji.all) {
      const position =
        active.entityId === null
          ? { x: world.selfX, y: world.selfY }
          : this.positionOf(world, active.entityId, now);
      if (position === null) continue;
      const sprite = this.assets.emojiSprite(active.index);
      if (sprite === null) continue;
      drawSprite(
        ctx,
        sprite,
        position.x - EMOJI_SIZE / 2,
        position.y + EMOJI_OFFSET_Y - EMOJI_SIZE / 2,
        EMOJI_SIZE,
        EMOJI_SIZE,
      );
    }
    ctx.restore();
  }

  drawEmojiBar(ctx: CanvasRenderingContext2D, camera: Camera, fontFamily: string): void {
    const uiWidth = camera.uiWidth;
    const uiHeight = camera.uiHeight;
    const button = emojiBarButton(uiWidth, uiHeight);

    const handle = this.assets.emojiSprite(EMOJI_BAR_ICON);
    if (handle !== null) {
      drawSprite(ctx, handle, button.x, button.y, button.w, button.h);
    } else {
      ctx.save();
      ctx.strokeStyle = TEAM_COLORS[0];
      ctx.strokeRect(button.x, button.y, button.w, button.h);
      ctx.restore();
    }

    if (!hitTest(button, this.pointerX, this.pointerY)) return;

    ctx.save();
    ctx.fillStyle = '#77EEFF';
    ctx.font = `10px ${fontFamily}, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    for (const slot of emojiBarSlots(uiWidth, uiHeight)) {
      const sprite = this.assets.emojiSprite(slot.index);
      if (sprite !== null) drawSprite(ctx, sprite, slot.x, slot.y, slot.w, slot.h);
      ctx.fillText(String(slot.index), slot.x - 2, slot.y);
    }
    ctx.restore();
  }
}

export { BURSTS, RIBBONS };
